import { getComponentClasses } from '@99designs/frontend-components/utils/app';
import 'lazysizes';
import React from 'react';
import { designPath } from '../../lib/platformUrls';
import { spaces } from '../../lib/styles';
import Heading from '../Heading';
import { Figure } from '../Image';
import { DesignCardSchema } from '../Schema/Schema';
import PlaceHolderImage from './PlaceHolderImage';

interface Props {
    variations?: string[];
    src?: string;
    alt?: string;
    width?: number;
    height?: number;
    ribbon?: React.ReactNode;
    attribution?: React.ReactNode;
    placeholderMessage?: string;
    designMetaContent?: React.ReactNode;
    lazyload?: boolean;
}

export default function DesignCard({
    variations = [],
    src,
    alt,
    width,
    height,
    ribbon,
    attribution,
    placeholderMessage,
    designMetaContent,
    lazyload = true,
}: Props) {
    const finalVariations = [...variations];
    if (!src) {
        finalVariations.push('placeholder');
    }

    const className = getComponentClasses('design-card', finalVariations);

    return (
        <div className={className}>
            {ribbon && <div className="design-card__ribbon">{ribbon}</div>}

            <div className="design-card__placeholder">
                <div className="design-card__placeholder__inner">
                    <PlaceHolderImage
                        width={84}
                        height={84}
                        style={{ marginBottom: placeholderMessage ? spaces.space8 : 0 }}
                    />

                    {!src && placeholderMessage && (
                        <Heading type="h5" variations={['h5', 'no-margin']}>
                            {placeholderMessage}
                        </Heading>
                    )}
                </div>
            </div>

            {src ? (
                <>
                    <DesignCardSchema url={src} width={width} height={height} name={alt} />
                    <div
                        className="design-card__image"
                        style={{ maxHeight: height, maxWidth: width }}
                    >
                        <Figure
                            src={src}
                            alt={alt}
                            width={width}
                            height={height}
                            caption={designMetaContent}
                            lazyload={lazyload}
                        />
                    </div>
                </>
            ) : (
                <div
                    className="design-card__image"
                    style={{ maxHeight: height, maxWidth: width }}
                />
            )}

            {attribution && <div className="design-card__attribution">{attribution}</div>}
        </div>
    );
}

interface DesignCardMetaProps {
    designId: number;
    designTitle: string;
    designDescription: string;
    designerId: string;
}

export function DesignCardMeta({
    designId,
    designTitle,
    designDescription,
    designerId,
}: DesignCardMetaProps) {
    return (
        <div className="design-card__meta">
            <a className="design-card__meta__cta" href={designPath(designerId, designId)}>
                <div className="design-card__meta__cta__content">
                    <div className="design-card__meta__cta__content__text">
                        <p className="paragraph paragraph--bold paragraph--no-margin">
                            {designTitle}
                        </p>
                        <p className="paragraph paragraph--no-margin">{designDescription}</p>
                    </div>
                </div>
            </a>
        </div>
    );
}
